import React, { useEffect, useRef, useState } from "react";
import WeatherSlide from "./WeatherSlide";
import { WeatherCard } from "./WeatherCard";
import WeatherSlider from "./WeatherSlider";
import { IconSvgElement } from "@hugeicons/react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { MobileWeatherSlide } from "./MobileWeatherSlide";

export interface WeatherDay {
  date: string;
  tempMax: number;
  tempMin: number;
  icon: IconSvgElement;
  description: string;
  className: string;
}

interface WeatherCarouselProps {
  days: WeatherDay[];
  city: string;
}

export const WeatherCarousel: React.FC<WeatherCarouselProps> = ({
  days,
  city,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useMediaQuery("(max-width: 640px)");

  useEffect(() => {
    if (days.length > 1 && !isHovered) {
      timerRef.current = setTimeout(() => setCollapsed(true), 4000);
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }
    return undefined;
  }, [days.length, isHovered]);

  if (days.length === 0) return null;

  if (isMobile)
    return (
      <div className="w-full flex items-center gap-3 px-5 overflow-x-auto scrollbar-hide">
        {days.map((d, dIdx) => (
          <MobileWeatherSlide
            key={d.date}
            day={d}
            lastItem={days.length === dIdx + 1}
          />
        ))}
      </div>
    );

  if (days.length === 1) return <WeatherCard day={days[0]} />;

  if (days.length <= 5)
    return (
      <div
        className="relative flex items-center"
        onMouseEnter={() => {
          setIsHovered(true);
          setCollapsed(false);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          if (days.length > 1) {
            timerRef.current = setTimeout(() => setCollapsed(true), 4000);
          }
        }}
      >
        <WeatherCard day={days[0]} />

        <div
          className={`transition-all duration-500 overflow-hidden ${
            collapsed ? "max-w-0" : "max-w-96"
          }`}
        >
          <div className="shrink-0 flex items-center gap-3 pl-2 pr-3 mr-1">
            <div className="h-[47px] w-px bg-black-50"></div>
            {days.map((d) => (
              <WeatherSlide key={d.date} day={d} />
            ))}
          </div>
        </div>
      </div>
    );

  return (
    <WeatherSlider
      days={days}
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      setIsHovered={setIsHovered}
      timerRefCurrent={timerRef.current}
    />
  );
};
